import useGraphqlQuery from '@elementary/common-ui/hooks/useGraphqlQuery';
import useSelectedEnv from '@elementary/common-ui/hooks/useSelectedEnv';
import { gql } from 'graphql-request';
import { DEPENDENCIES_COUNTERS } from '@elementary/common-ui/constants/queryKeys';
import {
  DependenciesFilterOptions,
  DependenciesFiltersCountersData,
} from 'shared/components/DependenciesTable/types';

export const GetTableDependenciesCounters = gql`
  query GetDependenciesCounters($id: String!) {
    asset(id: $id) {
      all: dependencies {
        node {
          id
        }
      }
      withAccess: dependencies(filters: { withAccessOnly: true }) {
        node {
          id
        }
      }
      upstream: dependencies(filters: { downstreamHops: 0 }) {
        node {
          id
        }
      }
      downstream: dependencies(filters: { upstreamHops: 0, assetType: TABLE }) {
        node {
          id
        }
      }
      downstreamBIAssets: dependencies(
        filters: { upstreamHops: 0, assetType: EXPOSURE }
      ) {
        node {
          id
        }
      }
      criticalAssets: dependencies(filters: { isCritical: true }) {
        node {
          id
        }
      }
    }
  }
`;

export const GetColumnDependenciesCounters = gql`
  query GetDependenciesCounters($id: String!, $name: String!) {
    asset(id: $id) {
      column(name: $name) {
        all: dependencies {
          node {
            id
          }
        }
        withAccess: dependencies(filters: { withAccessOnly: true }) {
          node {
            id
          }
        }
        upstream: dependencies(filters: { downstreamHops: 0 }) {
          node {
            id
          }
        }
        downstream: dependencies(
          filters: { upstreamHops: 0, assetType: TABLE }
        ) {
          node {
            id
          }
        }
        downstreamBIAssets: dependencies(
          filters: { upstreamHops: 0, assetType: EXPOSURE }
        ) {
          node {
            id
          }
        }
        criticalAssets: dependencies(filters: { isCritical: true }) {
          node {
            id
          }
        }
      }
    }
  }
`;

type Props = {
  modelId?: string;
  resource: {
    type: string;
    name?: string;
  };
};

const useDependenciesCounters = ({ modelId, resource }: Props) => {
  const selectedEnvId = useSelectedEnv();

  const isColumnResource = resource.type === 'COLUMN';

  const getColumnName = (resourceName?: string): string => {
    if (!resourceName) {
      return '';
    }
    const segments = resourceName.split('.');
    return segments[segments.length - 1];
  };
  const columnName = isColumnResource
    ? getColumnName(resource?.name)
    : undefined;

  const queryKey = [DEPENDENCIES_COUNTERS, selectedEnvId, modelId, columnName];
  const { data, isLoading, isFetching, error } =
    useGraphqlQuery<DependenciesFiltersCountersData>({
      queryKey,
      query: isColumnResource
        ? GetColumnDependenciesCounters
        : GetTableDependenciesCounters,
      variables: {
        id: modelId,
        name: columnName,
      },
      enabled: !!modelId,
    });

  const asset = data?.asset;
  return {
    data: {
      [DependenciesFilterOptions.All]: isColumnResource
        ? asset?.column.all.length
        : asset?.all.length,
      [DependenciesFilterOptions.WithAccess]: isColumnResource
        ? asset?.column.withAccess.length
        : asset?.withAccess.length,
      [DependenciesFilterOptions.Upstream]: isColumnResource
        ? asset?.column.upstream.length
        : asset?.upstream.length,
      [DependenciesFilterOptions.Downstream]: isColumnResource
        ? asset?.column.downstream.length
        : asset?.downstream.length,
      [DependenciesFilterOptions.DownstreamBIAssets]: isColumnResource
        ? asset?.column.downstreamBIAssets.length
        : asset?.downstreamBIAssets.length,
      [DependenciesFilterOptions.CriticalAssets]: isColumnResource
        ? asset?.column.criticalAssets.length
        : asset?.criticalAssets.length,
    },
    isLoading,
    isFetching,
    error,
  };
};

export default useDependenciesCounters;
